import React, {useState} from 'react'
import {createPortal} from 'react-dom'
import {Modal} from 'react-bootstrap'
import {downloadTemplate} from '../../services/firebase'

const modalsRoot = document.getElementById('root-modals') || document.body
const languages = ['fr', 'en', 'es', 'it']

const Field = ({label, value, onChange, type = 'text', required = false, placeholder = '', disabled = false}) => (
  <div className='col-md-6'>
    <label className='d-flex align-items-center fw-semibold mb-2'>
      <span className={required ? 'required' : ''}>{label}</span>
    </label>
    <input
      type={type}
      className='form-control form-control-solid'
      value={value ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      onChange={event => onChange(event.target.value)}
    />
  </div>
)

const Switch = ({id, label, checked, onChange}) => (
  <div className='form-check form-switch form-check-custom form-check-solid'>
    <input
      className='form-check-input'
      type='checkbox'
      id={id}
      checked={Boolean(checked)}
      onChange={event => onChange(event.target.checked)}
    />
    <label className='form-check-label' htmlFor={id}>{label}</label>
  </div>
)

const Section = ({title, children}) => (
  <section className='border rounded p-5 mb-6'>
    <h3 className='fs-4 mb-5'>{title}</h3>
    {children}
  </section>
)

const LocalizedFields = ({label, values, onChange, onDownload, downloading}) => (
  <div className='mb-5'>
    <label className='fw-semibold mb-3'>{label}</label>
    <div className='row g-3'>
      {languages.map(language => (
        <div className='col-md-6' key={`${label}-${language}`}>
          <div className='input-group'>
            <span className='input-group-text text-uppercase'>{language}</span>
            <input
              type='text'
              className='form-control form-control-solid'
              aria-label={`${label} (${language})`}
              value={values?.[language] || ''}
              onChange={event => onChange(language, event.target.value)}
            />
            {onDownload && (
              <button
                type='button'
                className='btn btn-icon btn-light'
                title={`Download ${label.toLowerCase()} template (${language})`}
                aria-label={`Download ${label.toLowerCase()} template (${language})`}
                disabled={Boolean(downloading) || !values?.[language]?.trim()}
                onClick={() => onDownload(values[language], `${label}-${language}`)}
              >
                {downloading === `${label}-${language}`
                  ? <span className='spinner-border spinner-border-sm' aria-hidden='true' />
                  : <i className='bi bi-download fs-4' aria-hidden='true' />}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
)

const ChannelDetailModal = ({show, handleClose, channel, setChannel, onSubmit, onDelete, saving}) => {
  const [downloadingTemplate, setDownloadingTemplate] = useState(null)
  const [downloadError, setDownloadError] = useState('')

  const handleDownloadTemplate = async (templateId, field) => {
    setDownloadingTemplate(field)
    setDownloadError('')
    try {
      const response = await downloadTemplate(templateId.trim())
      if(response?.error) {
        const message = typeof response.error === 'string' ? response.error : response.error.message
        throw new Error(message || 'Unable to download template.')
      }
      if(!response?.url) throw new Error('The template service did not return a download URL.')
      window.location.assign(response.url)
    } catch(error) {
      setDownloadError(error.message || 'Unable to download template. Please try again.')
    } finally {
      setDownloadingTemplate(null)
    }
  }

  const templateDownloadProps = {onDownload: handleDownloadTemplate, downloading: downloadingTemplate}
  const updateData = fields => setChannel({...channel, ...fields})

  const updateSection = (section, field, value) => {
    updateData({
      [section]: {
        ...(channel?.[section] || {}),
        [field]: value,
      },
    })
  }

  const updateWallet = (provider, field, value) => {
    updateData({
      wallet: {
        ...(channel?.wallet || {}),
        [provider]: {
          ...(channel?.wallet?.[provider] || {}),
          [field]: value,
        },
      },
    })
  }

  const updateLocalized = (section, item, language, value) => {
    updateData({
      [section]: {
        ...(channel?.[section] || {}),
        [item]: {
          ...(channel?.[section]?.[item] || {}),
          [language]: value,
        },
      },
    })
  }

  return createPortal(
    <Modal
      id='kt_modal_channel'
      tabIndex={-1}
      dialogClassName='modal-dialog modal-dialog-centered modal-dialog-scrollable mw-1000px'
      show={show}
      onHide={handleClose}
      backdrop={true}
    >
      <div className='modal-header'>
        <div>
          <h2>{channel?._persisted ? 'Update Channel' : 'Create Channel'}</h2>
          <div className='text-muted'>Channel settings are used by purchases, email delivery, documents, and wallet passes.</div>
        </div>
      </div>

      <div className='modal-body py-lg-8 px-lg-8'>
        <Section title='General'>
          <div className='row g-5 mb-5'>
            <Field
              label='Channel ID'
              required={true}
              value={channel?.id}
              placeholder='e.g. assuranceski'
              disabled={channel?._persisted}
              onChange={value => updateData({id: value.toLowerCase()})}
            />
            <Field
              label='Localized copy namespace'
              value={channel?.localized_copy_namespace}
              onChange={value => updateData({localized_copy_namespace: value})}
            />
          </div>
          <div className='d-flex flex-wrap gap-8'>
            <Switch id='channel-enabled' label='Enabled' checked={channel?.enabled} onChange={enabled => updateData({enabled})} />
            <Switch id='channel-default' label='Default channel' checked={channel?.is_default} onChange={is_default => updateData({is_default})} />
            <Switch id='channel-promo-codes' label='Promo codes enabled' checked={channel?.promo_codes_enabled} onChange={promo_codes_enabled => updateData({promo_codes_enabled})} />
          </div>
        </Section>

        <Section title='Brand'>
          <div className='row g-5'>
            <Field label='Display name' required={true} value={channel?.brand?.display_name} onChange={value => updateSection('brand', 'display_name', value)} />
            <Field label='Legal publisher' value={channel?.brand?.legal_publisher} onChange={value => updateSection('brand', 'legal_publisher', value)} />
            <Field label='Copyright' value={channel?.brand?.copyright} onChange={value => updateSection('brand', 'copyright', value)} />
            <Field label='Logo alt text' value={channel?.brand?.logo_alt} onChange={value => updateSection('brand', 'logo_alt', value)} />
            <Field label='Primary color' value={channel?.brand?.primary_color} placeholder='#ffed00' onChange={value => updateSection('brand', 'primary_color', value)} />
            <Field label='Background color' value={channel?.brand?.background_color} placeholder='#000000' onChange={value => updateSection('brand', 'background_color', value)} />
          </div>
        </Section>

        <Section title='URLs'>
          <div className='row g-5'>
            <Field label='Public domain' value={channel?.urls?.public_domain} placeholder='example.com' onChange={value => updateSection('urls', 'public_domain', value)} />
            <Field label='API base URL' value={channel?.urls?.api_base_url} onChange={value => updateSection('urls', 'api_base_url', value)} />
            <Field label='Functions base URL' value={channel?.urls?.functions_base_url} onChange={value => updateSection('urls', 'functions_base_url', value)} />
            <Field label='Upload path' value={channel?.urls?.upload_path} onChange={value => updateSection('urls', 'upload_path', value)} />
            <Field label='Privacy path' value={channel?.urls?.privacy_path} onChange={value => updateSection('urls', 'privacy_path', value)} />
            <Field label='Password reset path' value={channel?.urls?.password_reset_path} onChange={value => updateSection('urls', 'password_reset_path', value)} />
            <Field label='Basket path' value={channel?.urls?.basket_path} onChange={value => updateSection('urls', 'basket_path', value)} />
            <Field label='Checkout success path' value={channel?.urls?.checkout_success_path} onChange={value => updateSection('urls', 'checkout_success_path', value)} />
          </div>
        </Section>

        <Section title='Email'>
          <div className='row g-5'>
            <Field label='Sender name' value={channel?.email?.sender_name} onChange={value => updateSection('email', 'sender_name', value)} />
            <Field label='From address' type='email' value={channel?.email?.from_address} onChange={value => updateSection('email', 'from_address', value)} />
            <Field label='Reply-to address' type='email' value={channel?.email?.reply_to} onChange={value => updateSection('email', 'reply_to', value)} />
            <Field label='Mailgun domain' value={channel?.email?.mailgun_domain} onChange={value => updateSection('email', 'mailgun_domain', value)} />
            <Field label='Template' value={channel?.email?.template} onChange={value => updateSection('email', 'template', value)} />
          </div>
        </Section>

        <Section title='Contact form'>
          <label className='fw-semibold mb-2' htmlFor='channel-contact-primary-recipient'>Primary recipient</label>
          <input
            id='channel-contact-primary-recipient'
            type='email'
            className='form-control form-control-solid mb-4'
            value={channel?.contact_form?.primary_recipient || ''}
            onChange={event => updateSection('contact_form', 'primary_recipient', event.target.value)}
            disabled={saving}
          />
          <label className='fw-semibold mb-2' htmlFor='channel-contact-recipients'>Secondary recipients</label>
          <textarea
            id='channel-contact-recipients'
            className='form-control form-control-solid'
            rows={4}
            value={(channel?.contact_form?.secondary_recipients || []).join('\n')}
            onChange={event => updateSection('contact_form', 'secondary_recipients', event.target.value.split('\n'))}
            aria-describedby='channel-contact-recipients-help'
            disabled={saving}
          />
          <div id='channel-contact-recipients-help' className='text-muted mt-2'>
            Enter email addresses separated by new lines or commas. Secondary recipients receive a hidden copy (BCC).
          </div>
        </Section>

        <Section title='Localized email assets'>
          <LocalizedFields
            label='Apple Wallet button URL'
            values={channel?.email_assets?.apple_wallet_button}
            onChange={(language, value) => updateLocalized('email_assets', 'apple_wallet_button', language, value)}
          />
          <LocalizedFields
            label='Google Wallet button URL'
            values={channel?.email_assets?.google_wallet_button}
            onChange={(language, value) => updateLocalized('email_assets', 'google_wallet_button', language, value)}
          />
        </Section>

        <Section title='Wallet passes'>
          <div className='mb-6'>
            <Switch
              id='channel-wallet-enabled'
              label='Wallet passes enabled'
              checked={channel?.wallet?.enabled}
              onChange={enabled => updateSection('wallet', 'enabled', enabled)}
            />
          </div>
          <h4 className='fs-5 mb-4'>Apple Wallet</h4>
          <div className='row g-5 mb-7'>
            <Field label='Pass type identifier' value={channel?.wallet?.apple?.pass_type_identifier} onChange={value => updateWallet('apple', 'pass_type_identifier', value)} />
            <Field label='Logo URL' value={channel?.wallet?.apple?.logo_url} onChange={value => updateWallet('apple', 'logo_url', value)} />
            <Field label='Icon URL' value={channel?.wallet?.apple?.icon_url} onChange={value => updateWallet('apple', 'icon_url', value)} />
            <Field label='Foreground color' value={channel?.wallet?.apple?.foreground_color} onChange={value => updateWallet('apple', 'foreground_color', value)} />
            <Field label='Background color' value={channel?.wallet?.apple?.background_color} onChange={value => updateWallet('apple', 'background_color', value)} />
            <Field label='Label color' value={channel?.wallet?.apple?.label_color} onChange={value => updateWallet('apple', 'label_color', value)} />
          </div>
          <h4 className='fs-5 mb-4'>Google Wallet</h4>
          <div className='row g-5'>
            <Field label='Issuer ID' value={channel?.wallet?.google?.issuer_id} onChange={value => updateWallet('google', 'issuer_id', value)} />
            <Field label='Class ID' value={channel?.wallet?.google?.class_id} onChange={value => updateWallet('google', 'class_id', value)} />
            <Field label='Hero image URL' value={channel?.wallet?.google?.hero_image_url} onChange={value => updateWallet('google', 'hero_image_url', value)} />
            <Field label='Logo URL' value={channel?.wallet?.google?.logo_url} onChange={value => updateWallet('google', 'logo_url', value)} />
            <Field label='Background color' value={channel?.wallet?.google?.background_color} onChange={value => updateWallet('google', 'background_color', value)} />
          </div>
        </Section>

        <Section title='Document templates'>
          <div className='text-muted mb-5'>Enter the localized template document ID for each generated document.</div>
          {downloadError && <div className='alert alert-danger' role='alert'>{downloadError}</div>}
          <LocalizedFields {...templateDownloadProps} label='Purchase summary' values={channel?.documents?.PURCHASE_SUMMARY} onChange={(language, value) => updateLocalized('documents', 'PURCHASE_SUMMARY', language, value)} />
          <LocalizedFields {...templateDownloadProps} label='Purchase order' values={channel?.documents?.PURCHASE_ORDER} onChange={(language, value) => updateLocalized('documents', 'PURCHASE_ORDER', language, value)} />
          <LocalizedFields {...templateDownloadProps} label='Invoice' values={channel?.documents?.INVOICE} onChange={(language, value) => updateLocalized('documents', 'INVOICE', language, value)} />
          <LocalizedFields {...templateDownloadProps} label='ANCAV purchase summary' values={channel?.documents?.PURCHASE_SUMMARY_ANCAV} onChange={(language, value) => updateLocalized('documents', 'PURCHASE_SUMMARY_ANCAV', language, value)} />
          <LocalizedFields {...templateDownloadProps} label='ANCAV invoice' values={channel?.documents?.INVOICE_ANCAV} onChange={(language, value) => updateLocalized('documents', 'INVOICE_ANCAV', language, value)} />
        </Section>
      </div>

      <div className='modal-footer d-flex justify-content-between'>
        <div>
          {channel?._persisted && (
            <button type='button' className='btn btn-danger' onClick={onDelete} disabled={saving}>Delete</button>
          )}
        </div>
        <div className='d-flex gap-3'>
          <button type='button' className='btn' onClick={handleClose} disabled={saving}>Close</button>
          <button type='button' className='btn btn-primary' onClick={onSubmit} disabled={saving}>
            {saving ? 'Saving…' : channel?._persisted ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </Modal>,
    modalsRoot
  )
}

export default ChannelDetailModal
